<script lang="ts" setup>
import isNil from 'lodash/isNil'
import { onMounted, ref } from 'vue'
import { useRouter } from 'vue-router'

import BusyIndicator from '@/components/common/BusyIndicator.vue'
import NotificationBanner from '@/components/common/NotificationBanner.vue'
import TileSelector from '@/components/common/TileSelector.vue'
import { useLeaguesStore } from '@/store/public/leagues'
import { League } from '@/store/public/leagues.types'
import { getErrorMessage } from '@/utils/error-handling'

const leagues = useLeaguesStore()
const router = useRouter()

const errorMessage = ref<string | null>(null)
const isLoading = ref<boolean>(false)

function navigateToLeague(league: League): void {
  router.push({ path: `/leagues/${league.season}/${league.id}` })
}

function navigateToSeason(season: string): void {
  router.push({ path: `/leagues/${season}` })
}

onMounted(async () => {
  errorMessage.value = null
  isLoading.value = true

  try {
    await leagues.fetch()
  } catch (e) {
    const message = getErrorMessage(e as Error)
    console.error(`Failed fetching leagues: ${message}`)
    errorMessage.value = message
  }

  isLoading.value = false
})
</script>

<template>
  <h1 v-if="isLoading" class="busy-indicator-container">
    <BusyIndicator />
  </h1>

  <NotificationBanner
    v-else-if="errorMessage"
    :message="errorMessage"
    is-danger
  />

  <TileSelector
    v-else-if="isNil(leagues.selectedSeason)"
    :items="leagues.availableSeasons"
    @click="navigateToSeason"
  />

  <NotificationBanner
    v-else-if="leagues.availableGroups.length === 0"
    :message="$t('leagues.errors.no-leagues')"
    is-warning
  />

  <div v-for="(group, i) in leagues.availableGroups" v-else :key="`group-${i}`">
    <h3>{{ group.name }}</h3>

    <TileSelector
      :items="group.leagues"
      label-property="name"
      @click="navigateToLeague"
    />
  </div>
</template>

<style lang="scss" scoped>
.busy-indicator-container {
  text-align: center;
}
</style>
